<template>
    <div v-if="automatedPaymentSettings && automatedPaymentSettings.properties" class="payment-gateway-page-wrapper">
        <div :data-show-when-process="showWhenProcess.toString()" class="settings-wrapper">
            <b-card title="" class="p-2" no-body>
                <template #header>
                    <div class="d-flex justify-content-between align-items-center w-100">
                        <div class="d-flex align-items-start">
                            <h6 class="text-primary mr-2"><b>Automated Payments</b></h6>
                        </div>
                    </div>
                </template>
                <b-card-body class="p-2">
                    <b-row>
                        <b-col>
                            <form-square-switch
                                class="automated-payments-switch"
                                :labels="{
                                    checked: 'ENABLED',
                                    unchecked: 'DISABLED',
                                }"
                                @change="showEnableConfirmationModal"
                                v-model="automatedPaymentSettings.enabled"
                                :isToolbar="true"
                            />
                        </b-col>
                    </b-row>
                </b-card-body>
            </b-card>
            <b-card title="" class="p-2" no-body>
                <template #header>
                    <div class="d-flex justify-content-between align-items-center w-100">
                        <div class="d-flex align-items-start">
                            <h6 class="text-primary mr-2"><b>Confirmation Payments</b></h6>
                        </div>
                    </div>
                </template>
                <b-card-body class="p-2">
                    <b-row>
                        <b-col>
                            <form-dropdown
                                class="mb-0"
                                v-model="automatedPaymentSettings.process_pending"
                                :options="processOptions"
                                :disabled="!isAutomatedPaymentsEnabled"
                            />
                        </b-col>
                    </b-row>
                </b-card-body>
            </b-card>
            <b-card title="" class="p-2" no-body>
                <template #header>
                    <div class="d-flex justify-content-between align-items-center w-100">
                        <div class="d-flex align-items-start">
                            <h6 class="text-primary mr-2"><b>Remaining Balance Payments</b></h6>
                        </div>
                    </div>
                </template>
                <b-card-body class="p-2">
                    <b-row>
                        <b-col>
                            <form-dropdown
                                class="mb-0"
                                v-model="automatedPaymentSettings.process_balances_due"
                                :options="processOptions"
                                :disabled="!isAutomatedPaymentsEnabled"
                            />
                        </b-col>
                    </b-row>
                </b-card-body>
            </b-card>
            <b-card v-if="showWhenProcess" title="" class="p-2" no-body>
                <template #header>
                    <div class="d-flex justify-content-between align-items-center w-100">
                        <div class="d-flex align-items-start">
                            <h6 class="text-primary mr-2"><b>When?</b></h6>
                        </div>
                    </div>
                </template>
                <b-card-body class="p-2">
                    <b-row>
                        <b-col>
                            <form-dropdown
                                class="mb-0"
                                v-model="automatedPaymentSettings.process_after"
                                :options="processAfterOptions"
                                :disabled="!isAutomatedPaymentsEnabled"
                            />
                        </b-col>
                    </b-row>
                </b-card-body>
            </b-card>
        </div>
        <b-card title="" class="p-2 my-3" no-body>
            <template #header>
                <div class="d-flex justify-content-between align-items-center w-100">
                    <div class="d-flex align-items-start">
                        <h6 class="text-primary mr-2"><b>Pre-Auth Payments</b></h6>
                    </div>
                </div>
            </template>
            <b-card-body class="p-2">
                <b-row>
                    <b-col md="2">
                        <form-dropdown
                            class="mb-0"
                            v-model="automatedPaymentSettings.process_pre_auth"
                            :options="processOptions"
                            :disabled="!isAutomatedPaymentsEnabled"
                        />
                    </b-col>
                    <b-col v-if="showWhenProcessPreAuth" md="1" class="d-flex justify-content-center mt-2 mb-2">
                        When?
                    </b-col>
                    <b-col v-if="showWhenProcessPreAuth" md="9">
                        <form-dropdown class="mb-0" value="ON_ARRIVAL_DAY" :options="processPreAuthOptions" />
                    </b-col>
                </b-row>
            </b-card-body>
        </b-card>
        <b-card title="" class="p-2 my-3" no-body>
            <template #header>
                <div class="d-flex justify-content-between align-items-center w-100">
                    <div class="d-flex align-items-start">
                        <h6 class="text-primary mr-2"><b>Process for Properties</b></h6>
                        <fa-icon-tooltip
                            id="icon-info"
                            :icon="['fas', 'circle-info']"
                            class="text-primary"
                            variant="info"
                            size="md"
                        >
                            <span style="text-align: left">
                                You can select properties which will be included/excluded for processing.
                            </span>
                        </fa-icon-tooltip>
                    </div>
                </div>
            </template>
            <b-card-body class="p-2">
                <b-row>
                    <b-col md="2">
                        <form-dropdown
                            class="mb-0"
                            v-model="automatedPaymentSettings.properties_select_type"
                            :options="propertiesSelectTypeOptions"
                            :disabled="!isAutomatedPaymentsEnabled"
                        />
                    </b-col>
                    <b-col md="1" class="d-flex justify-content-center mt-2 mb-2"> For </b-col>
                    <b-col md="9">
                        <validation-provider
                            name="properties"
                            :rules="automatedPaymentSettings.properties_select_type !== 2 ? 'required' : ''"
                            v-slot="{ errors, handleChange }"
                        >
                            <form-dropdown
                                class="mb-0"
                                v-model="automatedPaymentSettings.properties"
                                :isSearch="true"
                                :options="propertiesOptions"
                                :multiSelect="true"
                                placeholder="Select Properties"
                                :error="errors[0]"
                                :disabled="
                                    automatedPaymentSettings.properties_select_type === 2 || !isAutomatedPaymentsEnabled
                                "
                                @input="(val) => handleChange(val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
            </b-card-body>
        </b-card>
        <b-card title="" class="p-2 my-3" no-body>
            <template #header>
                <div class="d-flex justify-content-between align-items-center w-100">
                    <div class="d-flex align-items-start">
                        <h6 class="text-primary mr-2"><b>If Payment Fails (Confirmation or Remaining Balance)</b></h6>
                    </div>
                </div>
            </template>
            <b-card-body class="p-2">
                <b-row>
                    <b-col>
                        <div class="d-flex align-items-start">
                            <form-checkbox
                                class="mb-0"
                                size="lg"
                                v-model="automatedPaymentSettings.send_payment_request"
                                :disabled="!isAutomatedPaymentsEnabled"
                            ></form-checkbox>
                            <h6 class="text-primary align-self-center mr-2 mb-0">Send Payment Request to Guest</h6>
                        </div>
                    </b-col>
                </b-row>
            </b-card-body>
        </b-card>
        <div class="submit-buttons-wrapper">
            <b-button variant="primary" @click="save">Save</b-button>
        </div>
        <modal
            :id="enableDisableConfirmationModalId"
            :title="enableDisableAutomatedPaymentsModalTitle"
            :ok-title="enableDisableText"
            @hidden="cancelEnableConfirmationModalChange"
            @ok="enableDisableAutomatedPayments"
        >
            <p>{{ enableDisableAutomatedPaymentsModalText }}</p>
        </modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Modal from '../../generic/modal/modal.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import FormDropdown from '../../generic/form-dropdown/form-dropdown.vue';
    import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
    import FaIconTooltip from '../../generic/fa-icon-tooltip/fa-icon-tooltip.vue';
    import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';
    import FormSearchMultiselect from '../../generic/form-search-multiselect/form-search-multiselect.vue';
    import { formatResponseError } from '../../../utils/api';

    export default {
        components: {
            Modal,
            FormSelect,
            FormDropdown,
            FaIconTooltip,
            FormCheckbox,
            FormSquareSwitch,
            FormSearchMultiselect,
        },
        name: 'PaymentGatewayAutomatedPayments',
        data() {
            return {
                enableDisableConfirmationModalId: 'enable-disable-confirmation-modal-id',
                enableDisableAutomatedPaymentsClicked: false,
                yesNoOptions: [
                    { text: 'Yes', value: true },
                    { text: 'No', value: false },
                ],
                processOptions: [
                    { text: 'Process Manually', value: false },
                    { text: 'Process Automatically', value: true },
                ],
                processAfterOptions: [
                    { text: 'As Soon As Possible', value: '' },
                    { text: 'After 24 Hours', value: 'TWENTY_FOUR_HOURS' },
                ],
                propertiesSelectTypeOptions: [
                    { value: 1, text: 'Process Only' },
                    { value: 0, text: 'Do Not Process' },
                    { value: 2, text: 'Process All' },
                ],
                processPreAuthOptions: [{ text: 'On Arrival Day', value: 'ON_ARRIVAL_DAY' }],
            };
        },
        computed: {
            ...mapGetters('paymentGateway', ['automatedPaymentSettings', 'propertiesOptions']),
            showWhenProcess() {
                return (
                    this.automatedPaymentSettings.process_balances_due || this.automatedPaymentSettings.process_pending
                );
            },
            showWhenProcessPreAuth() {
                return this.automatedPaymentSettings.process_pre_auth;
            },
            isAutomatedPaymentsEnabled() {
                return this.automatedPaymentSettings.enabled;
            },
            enableDisableText() {
                return this.isAutomatedPaymentsEnabled ? 'Enable' : 'Disable';
            },
            enableDisableAutomatedPaymentsModalTitle() {
                return `${this.enableDisableText} Automated Payments`;
            },
            enableDisableAutomatedPaymentsModalText() {
                return `Are you sure you want to ${this.enableDisableText.toLowerCase()} automated payments?`;
            },
        },
        mounted() {
            this.busy = true;
            Promise.all([this.fetchSettings(), this.fetchProperties()]);
            this.busy = false;
        },
        methods: {
            cancelEnableConfirmationModalChange() {
                if (!this.enableDisableAutomatedPaymentsClicked) {
                    this.automatedPaymentSettings.enabled = !this.automatedPaymentSettings.enabled;
                }
                this.enableDisableAutomatedPaymentsClicked = false;
            },
            showEnableConfirmationModal() {
                this.$bvModal.show(this.enableDisableConfirmationModalId);
            },
            async fetchSettings() {
                try {
                    await this.$store.dispatch('paymentGateway/fetchAutomatedPaymentSettings');
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not fetch automated payment settings.' });
                }
            },
            async fetchProperties() {
                try {
                    await this.$store.dispatch('paymentGateway/fetchProperties');
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not fetch properties.' });
                }
            },
            async enableDisableAutomatedPayments() {
                try {
                    this.$parent.$emit('busy', true);
                    this.enableDisableAutomatedPaymentsClicked = true;
                    await this.$store.dispatch(
                        'paymentGateway/updateAutomatedPaymentSettingsEnabled',
                        this.automatedPaymentSettings.enabled
                    );
                    this.fetchSettings();
                    this.successToast({
                        text: `Automated payments has been successfully ${this.enableDisableText.toLowerCase()}d.`,
                    });
                } catch (e) {
                    this.enableDisableAutomatedPaymentsClicked = false;
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text:
                            generic || `Could not ${this.enableDisableText.toLowerCase()} automated payment settings.`,
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async save() {
                try {
                    this.$parent.$emit('busy', true);
                    await this.$store.dispatch(
                        'paymentGateway/saveAutomatedPaymentSettings',
                        this.automatedPaymentSettings
                    );
                    this.successToast({ text: 'Settings has been successfully saved.' });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not save settings.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
        },
        watch: {
            'automatedPaymentSettings.properties_select_type': function (value, previousValue) {
                if (value && value !== previousValue && value === 2) {
                    this.automatedPaymentSettings.properties = [];
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    :deep(div.card-header) {
        padding: 0.5rem !important;
    }

    .automated-payments-switch {
        width: 10.65rem;
        :deep(.v-switch-label.v-right) {
            left: 2rem;
        }
    }

    .settings-wrapper {
        padding-top: 0.6rem;
        display: grid;
        grid-template-columns: 14rem repeat(3, 2fr);
        justify-items: stretch;
        column-gap: 1rem;
        row-gap: 1rem;
        @media (max-width: 1009px) {
            grid-template-columns: 1fr;
        }

        &[data-show-when-process='false'] {
            grid-template-columns: 14rem repeat(2, 2fr);
            @media (max-width: 848px) {
                grid-template-columns: 1fr;
            }
        }
    }
</style>
